import React, { useState } from "react";
import SnsModal from "../../../common/layouts/modal/SnsModal";
import Alert from "../../../common/layouts/modal/Alert";
import { ReactComponent as Info } from 'src/assets/info.svg';
import { ReactComponent as Twitter } from 'src/assets/author/x_author.svg';
import { ReactComponent as Instar } from 'src/assets/author/instar_author.svg';
import { ReactComponent as Link } from 'src/assets/author/link_author.svg';
import { ReactComponent as Follow } from 'src/assets/author/follow_author.svg';
import { motion } from "framer-motion";
import { triggerConfetti } from 'src/hooks/useConfetti';

interface AuthorDetailPageMobileProps {
    authors: any;
    isFollowing: boolean;
    onFollowToggle: () => void;
    followAlert: boolean;
    setFollowAlert: (value: boolean) => void;
}

const AuthorDetailPageMobile = ({
    authors,
    isFollowing,
    onFollowToggle,
    followAlert,
    setFollowAlert,
}: AuthorDetailPageMobileProps) => {
    if (!authors) return null;

    const testYn = authors.testStatus;
    const [showSnsAlert, setShowSnsAlert] = useState(false);

    const profileImageSrc = authors.profilePhoto
        ? `data:image/png;base64,${authors.profilePhoto}`
        : "/img/profile_sam.jpg";

    const authorStatus: string | null = localStorage.getItem("authorStatus");
    const authorEmail: string | null = localStorage.getItem("email");
    const authorYn: boolean = !!authorStatus && authorEmail === authors.authorEmail;

    const genres: string[] = authors.writingGenre
        ? authors.writingGenre.split(',').map((g: string) => g.trim()).filter(Boolean)
        : [];

    const hasSnsLinks =
        authors.brunchLink || authors.snsLink1 || authors.snsLink2 || authors.naverLink;

    return (
        <>
            {/* ── 히어로 섹션 ── */}
            <div
                className="w-full relative overflow-hidden"
                style={{ background: 'linear-gradient(180deg, #e0faeb 0%, #f0fdf6 55%, #ffffff 100%)' }}
            >
                {testYn && (
                    <div className="flex items-center gap-1.5 px-4 py-2 text-[#aaa] text-[11px] bg-[rgba(0,0,0,0.02)] border-b border-[rgba(0,0,0,0.04)]">
                        <Info className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>이 페이지는 예시 데이터로 구성되었습니다.</span>
                    </div>
                )}

                <div className="flex flex-col items-center px-5 pt-10 pb-10">

                    {/* 프로필 사진 */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.85 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.45, ease: [0.34, 1.2, 0.64, 1] }}
                        className="relative mb-5"
                    >
                        <div className="w-[96px] h-[96px] rounded-full border-[3px] border-white shadow-[0px_4px_16px_rgba(25,207,122,0.2)] overflow-hidden">
                            <img
                                src={profileImageSrc}
                                alt={authors.authorName}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        {/* 온라인 dot */}
                        <div className="absolute bottom-0.5 right-0.5 w-5 h-5 rounded-full bg-[#19cf7a] border-[2.5px] border-white shadow-sm" />
                    </motion.div>

                    {/* MBTI + 장르 태그 */}
                    {(authors.mbti || genres.length > 0) && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.1 }}
                            className="flex flex-row flex-wrap justify-center gap-1.5 mb-3"
                        >
                            {authors.mbti && (
                                <span className="bg-[rgba(25,207,122,0.12)] text-[#19cf7a] text-[13px] font-bold rounded-[8px] px-3 py-1">
                                    {authors.mbti}
                                </span>
                            )}
                            {genres.map((g, i) => (
                                <span key={i} className="bg-[#f3f4f6] text-[#4a5565] text-[13px] font-bold rounded-[8px] px-3 py-1">
                                    #{g}
                                </span>
                            ))}
                        </motion.div>
                    )}

                    {/* 작가 이름 */}
                    <motion.h1
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.15 }}
                        className="text-[#101828] text-[24px] font-bold text-center mb-2 leading-tight"
                    >
                        {authors.authorName}
                    </motion.h1>

                    {/* 작가 소개 */}
                    {authors.authorIntro && (
                        <motion.p
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="text-[#6a7282] text-[14px] text-center leading-[1.65] mb-5 px-2 line-clamp-3"
                        >
                            {authors.authorIntro}
                        </motion.p>
                    )}

                    {/* 팔로우 버튼 (SNS 보다 위에) */}
                    {!authorYn && (
                        <motion.button
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.25 }}
                            onMouseDown={(e) => {
                                // 로그인 상태 + 아직 팔로우 안 한 경우에만 폭죽
                                if (!isFollowing && authorStatus) {
                                    triggerConfetti(e, { count: 30, spread: 130 });
                                }
                            }}
                            onClick={onFollowToggle}
                            className={`flex items-center justify-center gap-2 h-[44px] w-[200px] rounded-[14px] text-white text-[15px] font-bold shadow-[0px_1px_3px_rgba(0,0,0,0.1)] transition-all active:scale-95 mb-4 ${
                                isFollowing
                                    ? 'bg-[#01863B] hover:bg-[#016b30]'
                                    : 'bg-[#19cf7a] hover:bg-[#00b368] shadow-[0px_4px_10px_rgba(25,207,122,0.3)]'
                            }`}
                        >
                            {!isFollowing && <Follow className="w-4 h-4" />}
                            {isFollowing ? '팔로우 중 ✓' : '팔로우'}
                        </motion.button>
                    )}

                    {/* SNS 아이콘 */}
                    {(authors.instagramLink || authors.xlink || hasSnsLinks) && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.4, delay: 0.3 }}
                            className="flex flex-row items-center gap-2"
                        >
                            {authors.instagramLink && (
                                <a
                                    href={authors.instagramLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group w-10 h-10 rounded-full border border-[#e5e7eb] flex items-center justify-center bg-white shadow-sm hover:!border-[#19cf7a] hover:-translate-y-1 hover:scale-108 hover:shadow-[0_0_15px_rgba(25,207,122,0.25)] transition-all duration-300 ease-out cursor-pointer [&_path]:transition-colors [&_path]:duration-300 [&_path]:group-hover:stroke-[#19cf7a]"
                                >
                                    <Instar className="w-5 h-5" />
                                </a>
                            )}
                            {authors.xlink && (
                                <a
                                    href={authors.xlink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group w-10 h-10 rounded-full border border-[#e5e7eb] flex items-center justify-center bg-white shadow-sm hover:!border-[#19cf7a] hover:-translate-y-1 hover:scale-108 hover:shadow-[0_0_15px_rgba(25,207,122,0.25)] transition-all duration-300 ease-out cursor-pointer [&_path]:transition-colors [&_path]:duration-300 [&_path]:group-hover:stroke-[#19cf7a]"
                                >
                                    <Twitter className="w-5 h-5" />
                                </a>
                            )}
                            {hasSnsLinks && (
                                <button
                                    onClick={() => setShowSnsAlert(true)}
                                    className="group w-10 h-10 rounded-full border border-[#e5e7eb] flex items-center justify-center bg-white shadow-sm hover:!border-[#19cf7a] hover:-translate-y-1 hover:scale-108 hover:shadow-[0_0_15px_rgba(25,207,122,0.25)] transition-all duration-300 ease-out cursor-pointer [&_path]:transition-colors [&_path]:duration-300 [&_path]:group-hover:stroke-[#19cf7a]"
                                >
                                    <Link className="w-5 h-5" />
                                </button>
                            )}
                        </motion.div>
                    )}
                </div>
            </div>

            {/* SNS 모달 */}
            {showSnsAlert && (
                <SnsModal
                    title="SNS 링크"
                    onClose={() => setShowSnsAlert(false)}
                    brunchLink={authors.brunchLink}
                    snsLink1={authors.snsLink1}
                    snsLink2={authors.snsLink2}
                    naverLink={authors.naverLink}
                />
            )}

            {/* 팔로우 알림 */}
            {followAlert && (
                <Alert
                    title="로그인이 필요해요"
                    message={"작가를 팔로우하려면\n로그인이 필요합니다."}
                    onClose={() => setFollowAlert(false)}
                    showLoginOnConfirm={true}
                />
            )}
        </>
    );
};

export default AuthorDetailPageMobile;
